<script setup lang="ts">
import { onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { isInsideTelegram } from '@/telegramWebApp'
import { redirectTarget, useBrowserLogin } from '@/composables/useBrowserLogin'

const { t } = useI18n()
const auth = useAuthStore()
const route = useRoute()
const router = useRouter()

const { loginConfig, loginConfigState, loginReady, loginBusy, loginMessage, prepare, login } = useBrowserLogin()

onMounted(() => {
  if (auth.status !== 'authenticated' && !isInsideTelegram()) {
    void prepare()
  }
  // The router guard already resolves sign-in before landing here on the error path. This
  // covers reaching /auth directly (e.g. a refresh, or the redirect briefly rendering this page)
  // while already authenticated -- bounce straight through instead of showing the page at all.
  if (auth.status === 'authenticated') {
    router.replace(redirectTarget(route.query.redirect))
  }
})

function retry() {
  if (loginConfigState.value === 'failed') void prepare()
  void auth.signIn().then(() => {
    if (auth.status === 'authenticated') router.replace(redirectTarget(route.query.redirect))
  })
}
</script>

<template>
  <div class="auth-shell">
    <p v-if="auth.status === 'loading' || auth.status === 'idle'">{{ t('auth.signingIn') }}</p>
    <template v-else-if="auth.status === 'error'">
      <p class="error">{{ auth.errorMessage }}</p>
      <button @click="retry">{{ t('common.retry') }}</button>
      <p v-if="loginConfigState === 'off'" class="login-message" data-testid="login-off">
        {{ t('auth.loginNotSetUp') }}
      </p>
      <p v-else-if="loginConfigState === 'failed'" class="login-message" data-testid="login-failed">
        {{ t('auth.loginConfigFailed') }}
      </p>
      <template v-if="loginConfig">
        <button
          type="button"
          class="telegram-login"
          :disabled="!loginReady || loginBusy"
          @click="login"
        >
          {{ t('auth.loginWithTelegram') }}
        </button>
        <p v-if="loginMessage" class="login-message">{{ loginMessage }}</p>
      </template>
    </template>
  </div>
</template>

<style scoped>
.auth-shell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  text-align: center;
  padding: 1rem;
}

.error {
  color: var(--tg-destructive-text-color);
}

.login-message {
  margin: 0;
  color: var(--tg-hint-color);
}

button:disabled {
  opacity: 0.6;
  cursor: default;
}

button {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  border: 1px solid var(--tg-button-color);
  background: var(--tg-button-color);
  color: var(--tg-button-text-color);
  cursor: pointer;
}
</style>
